import React, { createContext, useContext, useEffect, useReducer } from 'react';

const LOCAL_STORAGE_KEY = 'gov-demo-cases';

export interface TaxPayerCase {
  caseName: string;
  status: 'Completed' | 'Not started' | 'Canceled' | 'In progress';
  taxYears: number[];
  notes?: string;
}

interface TaxCaseState {
  cases: TaxPayerCase[];
}

type TaxCaseAction =
  | { type: 'INIT_CASES'; payload: TaxPayerCase[] }
  | { type: 'ADD_CASE'; payload: TaxPayerCase }
  | { type: 'DELETE_CASE'; payload: { caseName: string } }
  | {
      type: 'UPDATE_CASE_STATUS';
      payload: { caseName: string; status: TaxPayerCase['status'] };
    }
  | {
      type: 'UPDATE_CASE_NOTES';
      payload: { caseName: string; notes: string };
    };

interface TaxCaseContextType {
  state: TaxCaseState;
  dispatch: React.Dispatch<TaxCaseAction>;
  addCase: (caseData: TaxPayerCase) => void;
  updateCaseStatus: (id: string, status: TaxPayerCase['status']) => void;
  updateCaseNotes: (id: string, notes: string) => void;
  deleteCase: (id: string) => void;
}

const initialState: TaxCaseState = {
  cases: [],
};

const TaxCaseContext = createContext<TaxCaseContextType | null>(null);

function taxCaseReducer(
  state: TaxCaseState,
  action: TaxCaseAction,
): TaxCaseState {
  switch (action.type) {
    case 'INIT_CASES':
      return {
        ...state,
        cases: action.payload,
      };
    case 'ADD_CASE':
      return {
        ...state,
        cases: [...state.cases, action.payload],
      };
    case 'DELETE_CASE':
      return {
        ...state,
        cases: state.cases.filter(
          (item) => item.caseName !== action.payload.caseName,
        ),
      };
    case 'UPDATE_CASE_STATUS':
      return {
        ...state,
        cases: state.cases.map((item) =>
          item.caseName === action.payload.caseName
            ? { ...item, status: action.payload.status }
            : item,
        ),
      };
    case 'UPDATE_CASE_NOTES':
      return {
        ...state,
        cases: state.cases.map((item) =>
          item.caseName === action.payload.caseName
            ? { ...item, notes: action.payload.notes }
            : item,
        ),
      };
    default:
      return state;
  }
}

export const TaxCaseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(taxCaseReducer, initialState);

  // Load initial data from localStorage
  useEffect(() => {
    try {
      const storedCases = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedCases) {
        dispatch({ type: 'INIT_CASES', payload: JSON.parse(storedCases) });
      }
    } catch (error) {
      console.error('Error initializing cases from localStorage:', error);
    }
  }, []);

  // Save to localStorage when state changes
  useEffect(() => {
    if (state.cases.length > 0) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.cases));
    }
  }, [state.cases]);

  // Helper functions to simplify state management from components
  const addCase = (caseData: TaxPayerCase) => {
    if (!caseData.status) {
      caseData.status = 'Not started';
    }
    dispatch({
      type: 'ADD_CASE',
      payload: caseData,
    });
  };

  const updateCaseStatus = (
    caseName: string,
    status: TaxPayerCase['status'],
  ) => {
    dispatch({
      type: 'UPDATE_CASE_STATUS',
      payload: { caseName, status },
    });
  };

  const updateCaseNotes = (caseName: string, notes: string) => {
    dispatch({
      type: 'UPDATE_CASE_NOTES',
      payload: { caseName, notes },
    });
  };

  const deleteCase = (caseName: string) => {
    dispatch({
      type: 'DELETE_CASE',
      payload: { caseName },
    });
  };

  return (
    <TaxCaseContext.Provider
      value={{
        state,
        dispatch,
        addCase,
        updateCaseStatus,
        updateCaseNotes,
        deleteCase,
      }}
    >
      {children}
    </TaxCaseContext.Provider>
  );
};

export const useTaxCaseStore = (): TaxCaseContextType => {
  const context = useContext(TaxCaseContext);
  if (!context) {
    throw new Error('useTaxCaseStore must be used within a TaxCaseProvider');
  }
  return context;
};
